import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { DreAccountClassificationResultDto, DreAccountLineDto, DreByAccountDto, DreByAccountLineDto, DreLineKey } from "@bi/types";
import { api } from "../../lib/api";
import { fmtBRL, fmtPct } from "../../lib/format";
import { useAuth } from "../../lib/auth";
import { accountLineKey, classificationCounts, dreAccountKpis, signedBRL } from "../../lib/dre-by-account";
import { KpiTile } from "../intelligence/KpiTile";
import { QueryErrorBanner } from "../QueryErrorBanner";
import { IconAlertTriangle, IconCalculator, IconChevronDown, IconChevronRight, IconInfo } from "../icons";

const money = (v: number) => signedBRL(v, fmtBRL);
const pctOf = (v: number | null) => (v == null ? "" : Math.abs(v) < 0.0005 ? fmtPct(0) : fmtPct(v * 100));

function Notice({ tone = "info", children }: { tone?: "info" | "warning"; children: ReactNode }) {
  const cls = tone === "warning"
    ? { box: "border-warning-100 bg-warning-50", icon: "text-warning-600", text: "text-warning-700" }
    : { box: "border-info-100 bg-info-50", icon: "text-info-600", text: "text-gray-700" };
  const Icon = tone === "warning" ? IconAlertTriangle : IconInfo;
  return (
    <div className={`rounded-lg border px-3 py-2.5 flex gap-2 ${cls.box}`}>
      <Icon className={`w-4 h-4 flex-shrink-0 mt-0.5 ${cls.icon}`} />
      <div className={`text-xs leading-snug ${cls.text}`}>{children}</div>
    </div>
  );
}

/** Seletor da linha do DRE de uma conta. Só aparece para quem pode reclassificar. */
function LineSelect({ account, currentLine, options, onChange, saving }: {
  account: DreAccountLineDto;
  currentLine: string;
  options: DreByAccountDto["lineOptions"];
  onChange: (code: number, line: DreLineKey | null) => void;
  saving: boolean;
}) {
  if (account.code == null) return <span className="text-[11px] text-gray-400">sem conta no lançamento</span>;
  const code = account.code;
  return (
    <select
      aria-label={`Linha do DRE da conta ${account.name}`}
      value={currentLine}
      disabled={saving}
      onChange={(e) => onChange(code, e.target.value === "__regra" ? null : (e.target.value as DreLineKey))}
      className="w-full sm:w-56 rounded-md border border-surface-border bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-tm-500 disabled:opacity-50"
    >
      {currentLine === "" && <option value="">Escolha a linha…</option>}
      {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      {account.classifiedBy === "manual" && <option value="__regra">Voltar para a regra automática</option>}
    </select>
  );
}

function AccountRows({ line, accounts, memo, canEdit, options, onChange, savingCode, revenue }: {
  line: string;
  accounts: DreAccountLineDto[];
  memo?: boolean;
  canEdit: boolean;
  options: DreByAccountDto["lineOptions"];
  onChange: (code: number, line: DreLineKey | null) => void;
  savingCode: number | null;
  revenue: number;
}) {
  if (accounts.length === 0) {
    return <p className="px-5 sm:pl-12 py-3 text-xs text-gray-400">Nenhuma conta com lançamento no período.</p>;
  }
  return (
    <ul className="divide-y divide-surface-border/60">
      {accounts.map((a) => (
        <li key={a.code ?? "sem-conta"} className="px-5 sm:pl-12 py-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
          <div className="min-w-0 flex-1 basis-56">
            <p className={`text-sm truncate ${memo ? "text-gray-500 italic" : "text-gray-700"}`} title={a.name}>{a.name}</p>
            <p className="text-[11px] text-gray-400 tabular-nums">
              {a.code == null ? "rateio sem conta financeira no Sinersoft" : (
                <>{a.code}{a.mask ? ` · ${a.mask}` : ""} · {a.classifiedBy === "manual" ? "classificação definida" : "regra automática"}</>
              )}
              {memo ? " · informativo, não soma" : ""}
            </p>
          </div>
          {canEdit && (
            <LineSelect account={a} currentLine={accountLineKey(line)} options={options} onChange={onChange} saving={savingCode === a.code} />
          )}
          <span className="ml-auto w-32 text-right text-sm tabular-nums text-gray-800">{money(a.value)}</span>
          <span className="hidden sm:inline w-14 text-right text-xs tabular-nums text-gray-400">{revenue > 0 ? pctOf(Math.abs(a.value) / revenue) : ""}</span>
        </li>
      ))}
    </ul>
  );
}

function LineRow({ l, open, onToggle, children }: { l: DreByAccountLineDto; open: boolean; onToggle: () => void; children: ReactNode }) {
  if (l.kind !== "line") {
    const result = l.kind === "result";
    return (
      <div className={`px-5 py-3 flex items-center gap-3 ${result ? "bg-tm-50 border-t-2 border-tm-200" : "bg-surface-muted/60"}`}>
        <span className="w-4 flex-shrink-0" aria-hidden="true" />
        <p className={`flex-1 text-sm ${result ? "font-semibold text-tm-900" : "font-semibold text-gray-900"}`}>{l.label}</p>
        <span className={`w-32 text-right text-sm font-semibold tabular-nums ${l.value < 0 ? "text-danger-600" : result ? "text-tm-900" : "text-gray-900"}`}>{money(l.value)}</span>
        <span className="hidden sm:inline w-14 text-right text-xs tabular-nums text-gray-500">{pctOf(l.pctOfRevenue)}</span>
      </div>
    );
  }
  const warn = l.key === "a_classificar";
  return (
    <div>
      <button type="button" onClick={onToggle} aria-expanded={open}
        className={`w-full px-5 py-2.5 flex items-center gap-3 text-left hover:bg-surface-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tm-500 ${warn ? "bg-warning-50" : ""}`}>
        <span className="w-4 flex-shrink-0 text-gray-400">{open ? <IconChevronDown className="w-4 h-4" /> : <IconChevronRight className="w-4 h-4" />}</span>
        <span className="flex-1 min-w-0">
          <span className={`block text-sm ${warn ? "text-warning-800 font-medium" : "text-gray-700"}`}>{l.label}</span>
          <span className="block text-[11px] text-gray-400">
            {l.source ?? `${l.accounts.length} ${l.accounts.length === 1 ? "conta" : "contas"}`}
          </span>
        </span>
        <span className="w-32 text-right text-sm tabular-nums text-gray-900">{money(l.value)}</span>
        <span className="hidden sm:inline w-14 text-right text-xs tabular-nums text-gray-400">{pctOf(l.pctOfRevenue)}</span>
      </button>
      {open && (
        <div className="bg-surface-muted/30 border-t border-surface-border/60">
          {l.note && <p className="px-5 sm:pl-12 pt-2.5 text-xs text-gray-500">{l.note}</p>}
          {children}
        </div>
      )}
    </div>
  );
}

/**
 * DRE gerencial por conta financeira: receita e ICMS do CT-e, despesas pelo
 * rateio de contas a pagar (mesma leitura do Demonstrativo do Sinersoft), cada
 * conta na linha do DRE definida no de-para. Quem é admin ou gestor reclassifica
 * a conta ali mesmo, e a mudança vale para todos os períodos.
 */
export function DreByAccountPanel({ from, to }: { from: string; to: string }) {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "gestor";
  const qc = useQueryClient();
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [outsideOpen, setOutsideOpen] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);

  const key = ["transport", "dre-by-account", from, to] as const;
  const { data: d, isLoading, isError, refetch } = useQuery({
    queryKey: key,
    queryFn: () => api.get<DreByAccountDto>(`/dashboards/transport/dre/by-account?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`),
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: ({ code, line }: { code: number; line: DreLineKey | null }) =>
      api.patch<DreAccountClassificationResultDto>(`/dashboards/financial-accounts/${code}/dre-line`, { dreLine: line }),
    onSuccess: (r) => {
      const label = d?.lineOptions.find((o) => o.key === r.effectiveLine)?.label ?? "A classificar";
      setFlash(`${r.name} agora está em ${label}. Vale para todos os períodos.`);
      void qc.invalidateQueries({ queryKey: ["transport", "dre-by-account"] });
    },
    onError: () => setFlash("Não consegui gravar a classificação. Tente de novo."),
  });
  const onChange = (code: number, line: DreLineKey | null) => save.mutate({ code, line });
  const savingCode = save.isPending ? save.variables?.code ?? null : null;

  if (isError) return <QueryErrorBanner what="o DRE por conta" onRetry={() => { void refetch(); }} />;

  const k = d ? dreAccountKpis(d) : null;
  const counts = d ? classificationCounts(d) : null;
  const toggle = (key: string) => setOpen((o) => ({ ...o, [key]: !o[key] }));
  const revenue = d?.totals.revenue ?? 0;

  return (
    <section className="space-y-3" aria-labelledby="dre-conta-titulo">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <p id="dre-conta-titulo" className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">DRE por conta financeira</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Receita e ICMS do CT-e. Despesas pelo rateio de contas a pagar, por data de entrada, como no Demonstrativo do Sinersoft.
          </p>
        </div>
        {counts && (
          <p className="text-xs text-gray-400">
            {counts.manual} contas com classificação definida, {counts.regra} pela regra automática
            {counts.semLinha > 0 ? `, ${counts.semLinha} a classificar` : ""}
          </p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiTile label="Receita líquida" value={k ? fmtBRL(k.netRevenue) : "—"} loading={isLoading}
          footnote={d ? `${d.cteCount.toLocaleString("pt-BR")} CT-e, menos ICMS` : undefined} />
        <KpiTile label="Margem de contribuição" value={k ? money(k.contribution) : "—"} loading={isLoading}
          status={k && k.contribution < 0 ? "critical" : null}
          footnote={k?.contributionPct != null ? `${fmtPct(k.contributionPct * 100)} da receita bruta, depois dos custos da viagem` : undefined} />
        <KpiTile label="Resultado operacional (EBITDA)" value={k ? money(k.ebitda) : "—"} loading={isLoading}
          status={k && k.ebitda < 0 ? "critical" : null}
          footnote={k?.ebitdaPct != null ? `${fmtPct(k.ebitdaPct * 100)} da receita bruta` : undefined} />
        <KpiTile label="Resultado do período" value={k ? money(k.result) : "—"} loading={isLoading}
          status={k && k.result < 0 ? "critical" : null}
          footnote={k?.resultPct != null ? `${fmtPct(k.resultPct * 100)} da receita bruta, com o financeiro` : undefined} />
      </div>

      {d && d.warnings.length > 0 && (
        <div className="space-y-1.5">
          {d.warnings.map((w, i) => <Notice key={w} tone={i === 0 ? "info" : "warning"}>{w}</Notice>)}
        </div>
      )}
      {flash && <Notice>{flash}</Notice>}

      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-surface-border flex flex-wrap items-start gap-2">
          <span className="mt-0.5 text-gray-400 flex-shrink-0"><IconCalculator className="w-4 h-4" /></span>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-gray-900">Cascata do resultado</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Clique numa linha para ver as contas que a compõem{canEdit ? " e mudar a linha de uma conta" : ""}. % sobre a receita bruta.
            </p>
          </div>
        </div>

        {isLoading || !d ? (
          <div className="p-5 space-y-2">{[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="h-8 rounded bg-gray-100 animate-pulse" />)}</div>
        ) : (
          <div className="divide-y divide-surface-border">
            {d.lines.map((l) => (
              <LineRow key={l.key} l={l} open={!!open[l.key]} onToggle={() => toggle(l.key)}>
                <AccountRows line={l.key} accounts={l.accounts} memo={l.accountsAreMemo} canEdit={canEdit}
                  options={d.lineOptions} onChange={onChange} savingCode={savingCode} revenue={revenue} />
              </LineRow>
            ))}
          </div>
        )}
      </div>

      {d && (
        <div className="card overflow-hidden">
          <button type="button" onClick={() => setOutsideOpen((v) => !v)} aria-expanded={outsideOpen}
            className="w-full px-5 py-4 flex items-center gap-3 text-left hover:bg-surface-muted/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-tm-500">
            <span className="text-gray-400">{outsideOpen ? <IconChevronDown className="w-4 h-4" /> : <IconChevronRight className="w-4 h-4" />}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-semibold text-gray-900">Fora do DRE</span>
              <span className="block text-xs text-gray-400">Financiamento, consórcio e compra para estoque. Sai do caixa, mas não é custo do período.</span>
            </span>
            <span className="w-32 text-right text-sm font-semibold tabular-nums text-gray-900">{money(d.outside.value)}</span>
          </button>
          {outsideOpen && (
            <div className="border-t border-surface-border/60 bg-surface-muted/30">
              <AccountRows line="fora" accounts={d.outside.accounts} canEdit={canEdit}
                options={d.lineOptions} onChange={onChange} savingCode={savingCode} revenue={revenue} />
            </div>
          )}
        </div>
      )}
    </section>
  );
}
